import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Box } from '../../../components/primitives/Box';
import { Button } from '../../../components/primitives/Button';
import { Divider } from '../../../components/primitives/Divider';
import { Input } from '../../../components/primitives/Input';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { GeneratingStep } from '../../onboarding/components/GeneratingStep';
import { ReviewPlanStep } from '../../onboarding/components/ReviewPlanStep';
import {
  ONBOARDING_KEYS,
  useApplyPlan,
  useGeneratePlan,
  useOnboardingProfile,
  useSaveProfile,
} from '../../onboarding/hooks';
import type { GeneratedPlan, OrganizationProfile } from '../../onboarding/types';
import { ProductCatalogEditor } from './ProductCatalogEditor';
import { SettingsSection } from './SettingsSection';

type Phase = 'edit' | 'generating' | 'review';

interface ContextForm {
  organizationName: string;
  targetMarketLocation: string;
  industry: string;
  productSummary: string;
  targetAudience: string;
  salesMotion: string;
  tone: string;
  website: string;
  languages: string;
}

const EMPTY_FORM: ContextForm = {
  organizationName: '',
  targetMarketLocation: '',
  industry: '',
  productSummary: '',
  targetAudience: '',
  salesMotion: '',
  tone: '',
  website: '',
  languages: '',
};

const REQUIRED_FIELDS: Array<keyof ContextForm> = [
  'organizationName',
  'targetMarketLocation',
  'industry',
  'productSummary',
  'targetAudience',
];

function profileToForm(profile: OrganizationProfile): ContextForm {
  return {
    organizationName: profile.organizationName ?? '',
    targetMarketLocation: profile.targetMarketLocation ?? '',
    industry: profile.industry ?? '',
    productSummary: profile.productSummary ?? '',
    targetAudience: profile.targetAudience ?? '',
    salesMotion: profile.salesMotion ?? '',
    tone: profile.tone ?? '',
    website: profile.website ?? '',
    languages: profile.languages.join(', '),
  };
}

export function BusinessContextSection() {
  const { backendUser, currentOrganization } = useAuth();
  const { t, language } = useTranslation();
  const queryClient = useQueryClient();
  const organizationId = currentOrganization?.id ?? '';

  const { data: profile } = useOnboardingProfile(organizationId);
  const saveProfileMutation = useSaveProfile();
  const generatePlanMutation = useGeneratePlan();
  const applyPlanMutation = useApplyPlan();

  const memberships: Array<{
    organization?: { id: string };
    organizationId?: string;
    id?: string;
    role: string;
  }> =
    ((backendUser as unknown as Record<string, unknown>)
      ?.organizationMembers as typeof memberships) ??
    ((backendUser as unknown as Record<string, unknown>)?.organizations as typeof memberships) ??
    [];
  const orgMembership = memberships.find(
    (o) => (o.organization?.id ?? o.organizationId ?? o.id) === organizationId,
  );
  const canEdit = orgMembership?.role === 'owner' || orgMembership?.role === 'admin';

  const [form, setForm] = useState<ContextForm>(EMPTY_FORM);
  const [phase, setPhase] = useState<Phase>('edit');
  const [plan, setPlan] = useState<GeneratedPlan | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showRegenerateOffer, setShowRegenerateOffer] = useState(false);
  const [planApplied, setPlanApplied] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm(profileToForm(profile));
    }
  }, [profile]);

  const baseline = profile ? profileToForm(profile) : EMPTY_FORM;
  const hasChanges = (Object.keys(EMPTY_FORM) as Array<keyof ContextForm>).some(
    (field) => form[field].trim() !== baseline[field].trim(),
  );
  const isValid = REQUIRED_FIELDS.every((field) => form[field].trim().length > 0);

  const setField = (field: keyof ContextForm) => (text: string) => {
    setForm((prev) => ({ ...prev, [field]: text }));
  };

  const handleSave = () => {
    if (!organizationId || !canEdit || !isValid || !hasChanges) return;

    const { languages, ...profileFields } = form;

    saveProfileMutation.mutate(
      {
        organizationId,
        ...profileFields,
        languages: languages
          .split(',')
          .map((language) => language.trim())
          .filter(Boolean),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ONBOARDING_KEYS.profile(organizationId) });
          setSaveStatus('success');
          setPlanApplied(false);
          setShowRegenerateOffer(true);
          setTimeout(() => setSaveStatus('idle'), 3000);
        },
        onError: () => {
          setSaveStatus('error');
          setTimeout(() => setSaveStatus('idle'), 4000);
        },
      },
    );
  };

  const runGenerate = () => {
    setShowRegenerateOffer(false);
    setPhase('generating');
    generatePlanMutation.mutate(
      { organizationId, outputLanguage: language },
      {
        onSuccess: (data) => {
          if (data.generatedPlan) {
            setPlan(data.generatedPlan);
            setPhase('review');
          } else {
            setPhase('edit');
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 4000);
          }
        },
      },
    );
  };

  const handleApply = () => {
    if (!plan) return;
    applyPlanMutation.mutate(
      { organizationId, plan, outputLanguage: language },
      {
        onSuccess: () => {
          setPlan(null);
          setPlanApplied(true);
          setPhase('edit');
        },
      },
    );
  };

  if (phase === 'generating') {
    return (
      <SettingsSection title={t('settings.businessContext.title')}>
        <GeneratingStep isError={generatePlanMutation.isError} onRetry={runGenerate} />
      </SettingsSection>
    );
  }

  if (phase === 'review' && plan) {
    return (
      <SettingsSection title={t('settings.businessContext.title')}>
        <ReviewPlanStep
          organizationId={organizationId}
          plan={plan}
          onChange={setPlan}
          onApply={handleApply}
          isApplying={applyPlanMutation.isPending}
        />
      </SettingsSection>
    );
  }

  return (
    <SettingsSection
      title={t('settings.businessContext.title')}
      description={t('settings.businessContext.subtitle')}
    >
      <Input
        label={t('onboardingWizard.profile.organizationNameLabel')}
        value={form.organizationName}
        onChangeText={setField('organizationName')}
        editable={canEdit}
        placeholder={t('onboardingWizard.profile.organizationNamePlaceholder')}
        testID="business_context_organizationName"
      />
      <Input
        label={t('onboardingWizard.profile.targetMarketLocationLabel')}
        value={form.targetMarketLocation}
        onChangeText={setField('targetMarketLocation')}
        editable={canEdit}
        placeholder={t('onboardingWizard.profile.targetMarketLocationPlaceholder')}
        testID="business_context_targetMarketLocation"
      />
      <Input
        label={t('onboardingWizard.profile.industryLabel')}
        value={form.industry}
        onChangeText={setField('industry')}
        editable={canEdit}
        placeholder={t('onboardingWizard.profile.industryPlaceholder')}
        testID="business_context_industry"
      />
      <Input
        label={t('onboardingWizard.profile.productSummaryLabel')}
        value={form.productSummary}
        onChangeText={setField('productSummary')}
        editable={canEdit}
        placeholder={t('onboardingWizard.profile.productSummaryPlaceholder')}
        multiline
        testID="business_context_productSummary"
      />
      <Input
        label={t('onboardingWizard.profile.targetAudienceLabel')}
        value={form.targetAudience}
        onChangeText={setField('targetAudience')}
        editable={canEdit}
        placeholder={t('onboardingWizard.profile.targetAudiencePlaceholder')}
        testID="business_context_targetAudience"
      />
      <Input
        label={t('onboardingWizard.profile.salesMotionLabel')}
        value={form.salesMotion}
        onChangeText={setField('salesMotion')}
        editable={canEdit}
        placeholder={t('onboardingWizard.profile.salesMotionPlaceholder')}
        testID="business_context_salesMotion"
      />
      <Input
        label={t('onboardingWizard.profile.toneLabel')}
        value={form.tone}
        onChangeText={setField('tone')}
        editable={canEdit}
        placeholder={t('onboardingWizard.profile.tonePlaceholder')}
        testID="business_context_tone"
      />
      <Input
        label={t('settings.businessContext.websiteLabel')}
        value={form.website}
        onChangeText={setField('website')}
        editable={canEdit}
        placeholder={t('settings.businessContext.websitePlaceholder')}
        autoCapitalize="none"
        testID="business_context_website"
      />
      <Input
        label={t('settings.businessContext.languagesLabel')}
        value={form.languages}
        onChangeText={setField('languages')}
        editable={canEdit}
        placeholder={t('settings.businessContext.languagesPlaceholder')}
        helperText={t('settings.businessContext.languagesHelper')}
        autoCapitalize="words"
        testID="business_context_languages"
      />

      {!canEdit && (
        <Box backgroundColor={colors.status.warning.bg} borderRadius="md" padding="md">
          <Text variant="bodySmall" color={colors.status.warning.main}>
            {t('settings.businessContext.readOnlyNotice')}
          </Text>
        </Box>
      )}

      {saveStatus === 'success' && (
        <Text variant="bodySmall" color={colors.status.success.main}>
          {t('settings.businessContext.savedSuccess')}
        </Text>
      )}
      {saveStatus === 'error' && (
        <Text variant="bodySmall" color={colors.status.error.main}>
          {t('settings.businessContext.saveError')}
        </Text>
      )}

      {planApplied && (
        <Text variant="bodySmall" color={colors.status.success.main}>
          {t('settings.businessContext.appliedSuccess')}
        </Text>
      )}

      {showRegenerateOffer && (
        <Box
          backgroundColor={colors.background.secondary}
          borderRadius="md"
          padding="md"
          testID="business_context_regenerate_offer"
        >
          <VStack spacing="sm">
            <Text variant="bodySmall" color={colors.text.primary} style={styles.offerTitle}>
              {t('settings.businessContext.regenerateOfferTitle')}
            </Text>
            <Text variant="bodySmall" color={colors.text.secondary}>
              {t('settings.businessContext.regenerateOfferBody')}
            </Text>
            <HStack spacing="sm">
              <Button
                variant="primary"
                size="sm"
                onPress={runGenerate}
                testID="business_context_regenerate"
                accessibilityLabel={t('settings.businessContext.regenerateCta')}
              >
                {t('settings.businessContext.regenerateCta')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onPress={() => setShowRegenerateOffer(false)}
                testID="business_context_regenerate_later"
                accessibilityLabel={t('settings.businessContext.regenerateLater')}
              >
                {t('settings.businessContext.regenerateLater')}
              </Button>
            </HStack>
          </VStack>
        </Box>
      )}

      {canEdit && (
        <Box style={styles.buttonRow}>
          <Button
            variant="primary"
            onPress={handleSave}
            loading={saveProfileMutation.isPending}
            disabled={saveProfileMutation.isPending || !hasChanges || !isValid}
            testID="business_context_save"
            accessibilityLabel={
              saveProfileMutation.isPending
                ? t('common.states.saving')
                : t('common.actions.saveChanges')
            }
          >
            {saveProfileMutation.isPending
              ? t('common.states.saving')
              : t('common.actions.saveChanges')}
          </Button>
        </Box>
      )}

      <Divider color={colors.border.muted} />

      <ProductCatalogEditor organizationId={organizationId} canEdit={canEdit} />
    </SettingsSection>
  );
}

const styles = StyleSheet.create({
  offerTitle: {
    fontWeight: '600',
  },
  buttonRow: {
    alignSelf: 'flex-start',
  },
});
